import { FormContainer } from '@strata/react-form-container';
import { FormSection } from '@strata/react-form-section';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { AuditHistory } from 'components/AuditHistory';
import { FormPage } from 'components/FormPage';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { get, getFile, post, postCustomFormData, put, useGet } from 'services/Http';

export const HomeScreenMessageForm = ({
    formTitle,
    listPageUrl,
    formatInitialValues,
    validationSchema,
    formatSubmitBody,
    formContent,
    previewContent,
}) => {
    const { id } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate(listPageUrl);

    const isCreate = id === CREATE_ROUTE;

    const [message, setMessage] = useState();
    useEffect(() => {
        if (!isCreate) {
            void (async () => {
                try {
                    const response = await get(`/announcement/admin/home_screen/message/${id}`);

                    // Download images if present
                    if (response?.images) {
                        response.images = await Promise.all(
                            response.images.map(
                                async (img) =>
                                    new File(
                                        [
                                            await getFile(
                                                `/announcement/admin/home_screen/message/${id}/images/${img}`,
                                            ),
                                        ],
                                        img,
                                    ),
                            ),
                        );
                    }

                    setMessage(response);
                } catch (err) {
                    handleError(`An error occurred fetching ${formTitle}.`, err);
                }
            })();
        }
    }, [id, isCreate, formTitle]);

    const auditHistoryData = useGet(
        !isCreate ? `/announcement/admin/home_screen/message/audit/${id}` : null,
    );

    const isLoading = !(isCreate || message);

    return (
        <Formik
            initialValues={formatInitialValues(message)}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={async (values) => {
                try {
                    const body = formatSubmitBody(values);

                    let response;
                    if (isCreate) {
                        response = await post('/announcement/admin/home_screen/message', body);
                    } else {
                        response = await put(`/announcement/admin/home_screen/message/${id}`, body);
                    }

                    // Upload images if present
                    if (values.images && response?.message_id) {
                        const formData = new FormData();
                        values.images.forEach((img) => {
                            formData.append('images', img);
                        });
                        await postCustomFormData(
                            `/announcement/admin/home_screen/message/${response.message_id}/images`,
                            formData,
                        );
                    }

                    SuccessToast({
                        message: `${formTitle} ${isCreate ? 'created' : 'updated'} successfully.`,
                    });

                    goHome();
                } catch (err) {
                    handleError(`Error saving ${formTitle}.`, err);
                }
            }}
        >
            {(formik) => (
                <FormPage
                    columns={[
                        <FormContainer
                            key="form"
                            formTitle={`${isCreate ? 'Create' : 'Edit'} ${formTitle}`}
                            isLoading={isLoading}
                            onFormSubmit={formik.handleSubmit}
                            isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                            onSecondaryButtonClick={goHome}
                            formLeftContent={formContent(formik)}
                        />,
                        <Fragment key="preview">
                            <FormSection
                                className="sds_home-screen-messaging__preview"
                                sectionTitle="Preview"
                                isLoading={isLoading}
                                content={previewContent(formik.values)}
                            />
                            <AuditHistory data={auditHistoryData} isCreate={isCreate} />
                        </Fragment>,
                    ]}
                />
            )}
        </Formik>
    );
};

HomeScreenMessageForm.propTypes = {
    formTitle: PropTypes.string.isRequired,
    listPageUrl: PropTypes.string.isRequired,
    formatInitialValues: PropTypes.func.isRequired,
    validationSchema: PropTypes.shape({}).isRequired,
    formatSubmitBody: PropTypes.func.isRequired,
    formContent: PropTypes.func.isRequired,
    previewContent: PropTypes.func.isRequired,
};
